import type {
  Company,
  CompanyAdmin,
  CompanyWithCounts,
  CreateCompanyInput,
  Invitation,
  InvitationWithInvitee,
  UpdateCompanyInput,
  UpdateWantedPasswordInput,
  WantedPasswordStatus,
} from "dutyon-server/types";
import { httpClient } from "./httpClient";

export const getCompanies = async (params?: { code?: string }): Promise<CompanyWithCounts[]> => {
  const query = params?.code ? `?code=${encodeURIComponent(params.code)}` : "";
  const response = await httpClient.get<CompanyWithCounts[]>(`/companies${query}`);
  return response.data;
};

export const findOneCompany = async ({ id }: { id: string }): Promise<Company | null> => {
  const response = await httpClient.get<Company | null>(`/companies/${id}`);
  return response.data;
};

export const createCompany = async (data: CreateCompanyInput): Promise<Company> => {
  const response = await httpClient.post<Company>("/companies", data);
  return response.data;
};

export const updateCompany = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateCompanyInput;
}): Promise<Company> => {
  const response = await httpClient.put<Company>(`/companies/${id}`, data);
  return response.data;
};

export const deleteCompany = async ({ id }: { id: string }): Promise<Company> => {
  const response = await httpClient.delete<Company>(`/companies/${id}`);
  return response.data;
};

export const createInvitation = async ({
  companyId,
}: {
  companyId: string;
}): Promise<Invitation> => {
  const response = await httpClient.post<Invitation>(`/companies/${companyId}/invitations`, {});
  return response.data;
};

export const getInvitations = async ({
  companyId,
  status,
}: {
  companyId: string;
  status?: string[];
}): Promise<InvitationWithInvitee[]> => {
  const params = new URLSearchParams();
  if (status && status.length > 0) {
    for (const s of status) {
      params.append("status", s);
    }
  }
  const query = params.toString() ? `?${params.toString()}` : "";
  const response = await httpClient.get<InvitationWithInvitee[]>(
    `/companies/${companyId}/invitations${query}`,
  );
  return response.data;
};

export const approveInvitation = async ({
  companyId,
  invitationId,
}: {
  companyId: string;
  invitationId: string;
}): Promise<Invitation> => {
  const response = await httpClient.post<Invitation>(
    `/companies/${companyId}/invitations/${invitationId}/approve`,
    {},
  );
  return response.data;
};

export const rejectInvitation = async ({
  companyId,
  invitationId,
}: {
  companyId: string;
  invitationId: string;
}): Promise<Invitation> => {
  const response = await httpClient.post<Invitation>(
    `/companies/${companyId}/invitations/${invitationId}/reject`,
    {},
  );
  return response.data;
};

export const getCompanyAdmins = async ({
  companyId,
}: {
  companyId: string;
}): Promise<CompanyAdmin[]> => {
  const response = await httpClient.get<CompanyAdmin[]>(`/companies/${companyId}/admins`);
  return response.data;
};

export const addCompanyAdmin = async ({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}): Promise<CompanyAdmin> => {
  const response = await httpClient.post<CompanyAdmin>(`/companies/${companyId}/admins`, {
    userId,
  });
  return response.data;
};

export const removeCompanyAdmin = async ({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}): Promise<CompanyAdmin> => {
  const response = await httpClient.delete<CompanyAdmin>(
    `/companies/${companyId}/admins/${userId}`,
  );
  return response.data;
};

export const getWantedPassword = async ({
  companyId,
}: {
  companyId: string;
}): Promise<WantedPasswordStatus> => {
  const response = await httpClient.get<WantedPasswordStatus>(
    `/companies/${companyId}/wanted-password`,
  );
  return response.data;
};

export const updateWantedPassword = async ({
  companyId,
  password,
}: {
  companyId: string;
  password: UpdateWantedPasswordInput["password"];
}): Promise<WantedPasswordStatus> => {
  const response = await httpClient.post<WantedPasswordStatus>(
    `/companies/${companyId}/wanted-password`,
    { password },
  );
  return response.data;
};
